import { WorkflowDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { MoreHorizontal, Pencil, TrashIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditWorkflowDialog } from "./forms/edit-workflow-dialog";
import { RemoveWorkflowDialog } from "./forms/remove-workflow-dialog";
import { useWorkflowsContext } from "./use-workflows-context";

export const WorkflowDataTable = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const [selectedWorkflow, setSelectedWorkflow] = useState<
    WorkflowDto | undefined
  >(undefined);

  const { workflowList, shopId, onUpdateWorkflow, onRemoveWorkflow } =
    useWorkflowsContext();

  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const closeRemoveModal = useCallback(() => {
    setSelectedWorkflow(undefined);
    setIsEditing(false);
  }, [setSelectedWorkflow]);

  const onSubmitRemoveWorkflow = (workflowId: string) => {
    setSelectedWorkflow(
      workflowList.find((workflow) => workflow.id === workflowId),
    );
    setIsRemoving(true);
  };

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const onEditWorkflow = (workflowId: string) => {
    setSelectedWorkflow(
      workflowList.find((workflow) => workflow.id === workflowId),
    );
    setIsEditing(true);
  };

  const closeEditModal = useCallback(() => {
    setSelectedWorkflow(undefined);
    setIsEditing(false);
  }, [setSelectedWorkflow]);

  const onClickWorkflowCard = useCallback(
    (workflowId: string) => {
      navigate(`/shops/${shopId}/workflows/${workflowId}`);
    },
    [navigate, workflowList],
  );

  return (
    <div className="h-full px-4 py-6 mx-auto space-y-16 max-w-7xl sm:px-6 md:px-8">
      {isEditing && selectedWorkflow && (
        <EditWorkflowDialog
          onClose={closeEditModal}
          isOpen={isEditing}
          selectedWorkflow={selectedWorkflow}
          onSubmit={onUpdateWorkflow}
        />
      )}
      {isRemoving && selectedWorkflow && (
        <RemoveWorkflowDialog
          onClose={closeRemoveModal}
          isOpen={isRemoving}
          selectedWorkflow={selectedWorkflow}
          onSubmit={onSubmitRemoveWorkflow}
        />
      )}
      <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="shadow border border-border sm:rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    {t("workflow.create.name")}
                  </th>

                  <th
                    scope="col"
                    className="py-3.5    text-sm font-semibold pr-6 text-gray-900 sm:pl-6"
                  >
                    <div>{t("workflow.status")}</div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workflowList.map((workflow) => (
                  <tr
                    key={workflow.name}
                    className="cursor-pointer hover:bg-gray-50 group"
                  >
                    <td
                      onClick={() => {
                        onClickWorkflowCard(workflow.id);
                      }}
                      className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                    >
                      {workflow.name}
                    </td>

                    <td
                      onClick={() => {
                        onClickWorkflowCard(workflow.id);
                      }}
                      className="flex justify-center py-4 pl-3 pr-4 text-sm font-medium sm:pr-6"
                    >
                      <div
                        className={`w-fit px-2 py-0.5 rounded ${
                          workflow.status == "ACTIVE"
                            ? "bg-green-100 text-green-600 "
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {t(`misc.${workflow.status.toLowerCase()}`)}
                      </div>
                    </td>
                    <td className="pr-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger className="invisible group-hover:visible z-40">
                          <MoreHorizontal className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          sideOffset={5}
                          className="z-50"
                        >
                          <DropdownMenuItem
                            onClick={() => {
                              onEditWorkflow(workflow.id);
                            }}
                            className="flex items-center px-2 py-1 text-sm text-gray-900 cursor-pointer hover:bg-gray-100"
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            {t("settings.integrations.webhooks.menu.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              onRemoveWorkflow(workflow.id);
                            }}
                            className="flex items-center px-2 py-1 text-sm text-red-500 cursor-pointer hover:bg-red-100"
                          >
                            <TrashIcon className="w-4 h-4 mr-1" />
                            {t("settings.integrations.webhooks.menu.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
